import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { Alert, Button, Card, Checkbox, Form, Input, InputNumber, List, Select, Space, Spin, Typography, message } from 'antd'
import { richPromotionApi } from 'api/richPromotion'
import { IRichContent, IRichMedia, IRichPromotion } from 'models/richPromotion'
import { IMAGES_URL } from 'shared/constants/urls'
import styles from './RichPromotionPage.module.scss'

const defaults = { active: false, extra_discount: 14, duration_minutes: 20, views_offset: 0, favorites_offset: 0, html: '' }

export const RichPromotionPage = () => {
  const { id = '' } = useParams<{ id: string }>()
  const [form] = Form.useForm()
  const [contentForm] = Form.useForm()
  const [promotion, setPromotion] = React.useState<IRichPromotion | null>(null)
  const [content, setContent] = React.useState<IRichContent[]>([])
  const [media, setMedia] = React.useState<IRichMedia[]>([])
  const [busy, setBusy] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')
  const [activationAvailable, setActivationAvailable] = React.useState(false)
  const [contentMedia, setContentMedia] = React.useState<string[]>([])
  const kind = Form.useWatch('kind', contentForm) || 'review'

  React.useEffect(() => {
    richPromotionApi.get(id).then(({ data }) => {
      setPromotion(data.promotion); setContent(data.content); setMedia(data.promotion?.media || [])
      setActivationAvailable(data.activation_available)
      form.setFieldsValue(data.promotion || defaults)
    }).catch(() => setError('Рич-акции недоступны. Нужен доступ к магазину Horse Planet в dev.')).finally(() => setLoading(false))
  }, [id, form])

  const save = async () => {
    setBusy(true)
    try {
      const values = await form.validateFields()
      const { data } = await richPromotionApi.save(id, values)
      setPromotion(data); message.success('Акция сохранена')
    } catch { message.error('Не удалось сохранить акцию. Проверьте поля и доступность сервера') }
    finally { setBusy(false) }
  }

  const upload = async (files: FileList | null, forContent: boolean) => {
    if (!files?.length) return
    if (!promotion) { message.info('Сначала сохраните настройки акции'); return }
    setBusy(true)
    try {
      const uploaded: IRichMedia[] = []
      for (const file of Array.from(files)) uploaded.push((await richPromotionApi.upload(id, file)).data)
      const next = [...media, ...uploaded]
      setMedia(next)
      if (forContent) setContentMedia(previous => [...previous, ...uploaded.map(item => item.key)])
      else {
        const document = new DOMParser().parseFromString(form.getFieldValue('html') || '', 'text/html')
        const resolveAsset = (value: string) => {
          const clean = value.trim().split(/[?#]/)[0]
          const asset = next.find(item => item.name === clean || item.name === clean.split('/').pop())
          return asset ? `${IMAGES_URL}${asset.key}` : value
        }
        const resolveCss = (css: string) => css.replace(/url\(\s*(['"]?)([^)'"\s]+)\1\s*\)/g, (_, quote, url) => `url("${resolveAsset(url)}")`)
        document.querySelectorAll('[src], [poster], [srcset], [style], style').forEach(element => {
          for (const attribute of ['src', 'poster']) {
            const value = element.getAttribute(attribute)
            if (value) element.setAttribute(attribute, resolveAsset(value))
          }
          const srcset = element.getAttribute('srcset')
          if (srcset) element.setAttribute('srcset', srcset.split(',').map(candidate => {
            const [url, ...descriptor] = candidate.trim().split(/\s+/)
            return [resolveAsset(url), ...descriptor].join(' ')
          }).join(', '))
          const style = element.getAttribute('style')
          if (style) element.setAttribute('style', resolveCss(style))
          if (element.tagName === 'STYLE') element.textContent = resolveCss(element.textContent || '')
        })
        form.setFieldValue('html', '<!doctype html>\n' + document.documentElement.outerHTML)
      }
      message.success('Медиа загружены')
    } catch { message.error('Ошибка загрузки. Поддерживаются JPG, PNG, WebP, MP4, WebM до 80 МБ') }
    finally { setBusy(false) }
  }

  const addContent = async () => {
    setBusy(true)
    try {
      const values = await contentForm.validateFields()
      await richPromotionApi.content(id, { ...values, media: contentMedia })
      setContent((await richPromotionApi.get(id)).data.content)
      contentForm.resetFields(); setContentMedia([]); message.success('Материал добавлен')
    } catch { message.error('Не удалось добавить материал') }
    finally { setBusy(false) }
  }

  if (loading) return <Spin />
  if (error) return <Alert type='error' message={error} />
  return <div className={styles.page}>
    <Link to={`/goods/edit/${id}`}>← К товару</Link>
    <Typography.Title level={2}>Рич-акция</Typography.Title>
    <Alert type='info' message='Одна попытка на покупателя. Таймер начинается при первом входе и не сбрасывается после редактирования акции. Доставка за счёт маркетплейса. Остаток не резервируется и списывается после оплаты. Акционная карточка скрывается, когда исходный товар заканчивается.' />
    {!activationAvailable && <Alert type='warning' message='Можно сохранить настройки, HTML, фото, видео, отзывы и вопросы. Включение акции временно недоступно: проверьте соединение с сервером.' />}
    <Form form={form} layout='vertical' initialValues={defaults} onFinish={save}>
      <Card title='Настройки акции'>
        <Form.Item name='active' valuePropName='checked'><Checkbox disabled={!activationAvailable}>Активировать</Checkbox></Form.Item>
        <div className={styles.fields}>
          <Form.Item label='Дополнительная скидка, %' name='extra_discount' rules={[{ required: true }]}><InputNumber min={0.01} max={99} precision={2} /></Form.Item>
          <Form.Item label='Продолжительность, минут' name='duration_minutes' rules={[{ required: true }]}><InputNumber min={1} max={1440} precision={0} /></Form.Item>
          <Form.Item label='Начальное число просмотров' name='views_offset'><InputNumber min={0} max={1000000000} precision={0} /></Form.Item>
          <Form.Item label='Начальное число добавлений в избранное' name='favorites_offset'><InputNumber min={0} max={1000000000} precision={0} /></Form.Item>
        </div>
        <Typography.Text type='secondary'>Выгода считается от текущей цены выбранной вариации. Реальные просмотры и избранное прибавляются к начальным значениям.</Typography.Text>
      </Card>
      <Card title='HTML и изображения'>
        <p>Сначала загрузите HTML, затем его изображения и видео. Ссылки на файлы в изображениях, видео и CSS заменятся адресами CDN при совпадении имён.</p>
        <label className={styles.upload}>HTML-файл <input type='file' accept='.html,text/html' disabled={busy} onChange={async event => {
          const file = event.target.files?.[0]
          if (!file) return
          if (file.size > 2000000) { message.error('HTML должен быть меньше 2 МБ'); return }
          form.setFieldValue('html', await file.text())
        }} /></label>
        <label className={styles.upload}>Изображения и видео HTML <input type='file' multiple accept='image/jpeg,image/png,image/webp,video/mp4,video/webm' disabled={busy || !promotion} onChange={event => upload(event.target.files, false)} /></label>
        <Form.Item name='html' label='HTML документа'><Input.TextArea rows={12} /></Form.Item>
        <Typography.Text type='secondary'>JavaScript, формы и внешние подключения в документе отключены. Картинки и видео загружаются с CDN.</Typography.Text>
        <List size='small' dataSource={media} renderItem={item => <List.Item><a href={`${IMAGES_URL}${item.key}`} target='_blank' rel='noreferrer'>{item.name || item.key}</a></List.Item>} />
      </Card>
      <Button type='primary' htmlType='submit' loading={busy}>Сохранить акцию</Button>
    </Form>
    <Card title='Отзывы и вопросы'>
      <Form form={contentForm} layout='vertical' initialValues={{ kind: 'review' }} onFinish={addContent}>
        <Form.Item name='kind' label='Тип'><Select options={[{ value: 'review', label: 'Отзыв' }, { value: 'question', label: 'Вопрос' }]} /></Form.Item>
        <Form.Item name='author' label='Имя автора'><Input maxLength={120} /></Form.Item>
        {kind === 'review' && <Form.Item name='rating' label='Оценка (если указана в исходном отзыве)'><InputNumber min={1} max={5} precision={0} /></Form.Item>}
        <Form.Item name='text' label={kind === 'review' ? 'Текст отзыва' : 'Вопрос'} rules={[{ required: true, whitespace: true }]}><Input.TextArea rows={4} maxLength={10000} /></Form.Item>
        {kind === 'question' && <Form.Item name='answer' label='Ответ продавца'><Input.TextArea rows={3} maxLength={10000} /></Form.Item>}
        <label className={styles.upload}>Фото и видео <input type='file' multiple accept='image/jpeg,image/png,image/webp,video/mp4,video/webm' disabled={busy || !promotion} onChange={event => upload(event.target.files, true)} /></label>
        <p>Прикреплено: {contentMedia.length}</p>
        <Button htmlType='submit' disabled={!promotion} loading={busy}>Добавить {kind === 'review' ? 'отзыв' : 'вопрос'}</Button>
      </Form>
      <List dataSource={content} renderItem={item => <List.Item><Space direction='vertical'><strong>{item.kind === 'review' ? `Отзыв${item.rating != null ? ` · ${item.rating}/5` : ''}` : 'Вопрос'} {item.author}</strong><p>{item.text}</p>{item.answer && <p>{item.answer}</p>}<Button danger onClick={async () => { await richPromotionApi.removeContent(id, item._id); setContent(previous => previous.filter(entry => entry._id !== item._id)) }}>Удалить</Button></Space></List.Item>} />
    </Card>
  </div>
}
